import type {
  PayeeInvoice,
  PayoutRail,
  SinglePayee,
  SinglePaymentChannel,
} from '@/types/portal';
import { balanceAfterAdvance } from './invoice';

/** Labels and copy for each single-payment destination channel. */
export const channelMeta: Record<
  SinglePaymentChannel,
  { label: string; short: string; icon: string; description: string }
> = {
  wallet: {
    label: 'Twiva Pay Wallet',
    short: 'Wallet',
    icon: 'ri-wallet-3-line',
    description: 'Instant transfer to a Twiva Pay Wallet',
  },
  mobile: {
    label: 'Mobile money',
    short: 'Mobile money',
    icon: 'ri-smartphone-line',
    description: 'Send to an M-Pesa, Airtel Money, Tigo Pesa or MTN MoMo number',
  },
  bank: {
    label: 'Bank account',
    short: 'Bank',
    icon: 'ri-bank-line',
    description: 'Direct settlement to an external bank account',
  },
  bill: {
    label: 'Pay a bill',
    short: 'Paybill',
    icon: 'ri-file-list-2-line',
    description: 'Pay a utility, paybill or service account',
  },
  airtime: {
    label: 'Buy airtime',
    short: 'Airtime',
    icon: 'ri-sim-card-line',
    description: 'Top up any mobile line with airtime',
  },
  goods: {
    label: 'Buy goods',
    short: 'Buy Goods',
    icon: 'ri-shopping-bag-3-line',
    description: 'Pay a merchant or till number',
  },
};

/** Payout rails that can deliver a given channel in a given currency. */
export function railsForChannel(
  rails: PayoutRail[],
  channel: SinglePaymentChannel,
  currency: string,
): PayoutRail[] {
  return rails.filter((rail) => rail.currency === currency && rail.channel === channel);
}

/** Whether a payee has the destination details an channel needs. */
export function payeeSupportsChannel(payee: SinglePayee, channel: SinglePaymentChannel): boolean {
  if (channel === 'wallet') return Boolean(payee.target.walletId);
  if (channel === 'mobile' || channel === 'airtime') return Boolean(payee.target.mobileNumber);
  return Boolean(payee.target.accountNumber);
}

export interface TargetValidation {
  valid: boolean;
  error: string;
  /** Mobile network inferred from the number's country prefix, when recognised. */
  network?: string;
}

/** Country prefix + default network for each mobile-money market we settle in. */
const MOBILE_PREFIX: Record<string, { prefix: string; network: string }> = {
  KES: { prefix: '254', network: 'M-Pesa' },
  TZS: { prefix: '255', network: 'M-Pesa' },
  UGX: { prefix: '256', network: 'MTN MoMo' },
};

const PREFIX_NETWORK: Record<string, string> = {
  '254': 'M-Pesa',
  '255': 'M-Pesa',
  '256': 'MTN MoMo',
};

/**
 * Inline-validate a destination target before a single payment can be confirmed.
 * Wallet IDs are short numeric codes, mobile numbers carry a country prefix and
 * bank accounts are 8–17 digits — a bad target is caught here, not at the rail.
 */
export function validateTarget(
  channel: SinglePaymentChannel,
  raw: string,
  currency?: string,
): TargetValidation {
  const value = raw.trim();
  if (!value) return { valid: false, error: 'Enter a destination.' };

  if (channel === 'wallet') {
    if (!/^\d+$/.test(value)) return { valid: false, error: 'Wallet IDs contain digits only.' };
    if (value.length < 4 || value.length > 8) {
      return { valid: false, error: 'Wallet IDs are 4–8 digits.' };
    }
    return { valid: true, error: '' };
  }

  if (channel === 'mobile') {
    if (!/^\d+$/.test(value)) {
      return { valid: false, error: 'Mobile numbers contain digits only.' };
    }
    const prefix = value.slice(0, 3);
    const network = PREFIX_NETWORK[prefix];
    if (!network || value.length !== 12) {
      return { valid: false, error: 'Use the full number with country code, e.g. 254712345678.' };
    }
    const expected = currency ? MOBILE_PREFIX[currency]?.prefix : undefined;
    if (expected && prefix !== expected) {
      return {
        valid: false,
        error: `That looks like a ${network} number — this wallet settles ${currency} payments.`,
      };
    }
    return { valid: true, error: '', network };
  }

  if (channel === 'bill') {
    if (!/^\d+$/.test(value)) {
      return { valid: false, error: 'Paybill numbers contain digits only.' };
    }
    if (value.length < 5 || value.length > 7) {
      return { valid: false, error: 'Paybill numbers are 5–7 digits.' };
    }
    return { valid: true, error: '' };
  }

  if (channel === 'airtime') {
    if (!/^\d+$/.test(value)) {
      return { valid: false, error: 'Phone numbers contain digits only.' };
    }
    if (value.length < 9 || value.length > 12) {
      return { valid: false, error: 'Use the full number with country code, e.g. 254712345678.' };
    }
    return { valid: true, error: '' };
  }

  if (channel === 'goods') {
    if (!/^\d+$/.test(value)) {
      return { valid: false, error: 'Till numbers contain digits only.' };
    }
    if (value.length < 5 || value.length > 7) {
      return { valid: false, error: 'Till numbers are 5–7 digits.' };
    }
    return { valid: true, error: '' };
  }

  if (!/^\d+$/.test(value)) {
    return { valid: false, error: 'Account numbers contain digits only.' };
  }
  if (value.length < 8 || value.length > 17) {
    return { valid: false, error: 'Bank account numbers are 8–17 digits.' };
  }
  return { valid: true, error: '' };
}

/** The approved invoice a campaign-linked payment reconciles against. */
export function invoiceForCampaign(
  payee: SinglePayee | null,
  campaignId: string | null,
): PayeeInvoice | null {
  if (!payee || !campaignId) return null;
  return payee.invoices.find((invoice) => invoice.campaignId === campaignId) ?? null;
}

/** What a campaign-linked payment settles once any early payout advance is netted off. */
export function reconciledAmount(invoice: PayeeInvoice | null): number {
  if (!invoice) return 0;
  return balanceAfterAdvance(invoice.amount, invoice.advance);
}